import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import { Dialog, DialogContent, DialogTitle } from "@rakazo/ui-web";
import { X } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { observeResize, onViewportResize } from "../../lib/shared-resize";
import { MessageShareMenu } from "./message-share-menu";

type OpenArticle = (text: string, trigger: HTMLButtonElement | null) => void;
const MessageArticleContext = createContext<OpenArticle | null>(null);

export function useMessageArticle(): OpenArticle {
  const open = useContext(MessageArticleContext);
  if (!open)
    throw new Error("Message article provider missing / Відсутній вузол повного повідомлення");
  return open;
}

/** What the chat can ask of the reader without owning it. / Що чат може попросити в вікна читання. */
export type MessageArticleControl = { close: () => void };

/** The reader outlives row enrichment, pagination and suspended transcript content.
 * Вікно читання переживає доповнення рядка, сторінки історії й очікування всередині чату. */
export function MessageArticleProvider({
  scope: chatScope,
  control,
  children,
}: {
  /** The chat the reader belongs to; null while the screen is loading it again. */
  scope: string | null;
  /** Filled with the reader's close, so sending a message can end the reading. */
  control?: RefObject<MessageArticleControl | null>;
  children: ReactNode;
}) {
  const { t } = useLingui();
  // A chat that is briefly unknown (the session or the bot list is being read again after a
  // send) is not another chat: the reader keeps the last one until a different chat shows up.
  // Тимчасово невідомий чат не є іншим чатом: вікно лишається, доки не з'явиться інший.
  const lastChat = useRef<string | null>(chatScope);
  if (chatScope !== null) lastChat.current = chatScope;
  const scope = lastChat.current ?? "";
  const [article, setArticle] = useState<{
    scope: string;
    text: string;
    trigger: HTMLButtonElement | null;
    open: boolean;
  } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [popup, setPopup] = useState<HTMLDivElement | null>(null);
  // Whether the person was last in the reader or a menu opened from it (React bubbles focus
  // from its portals); a hidden popup no longer contains the focus, so remember it.
  // Чи був користувач востаннє у вікні або його меню; приховане вікно вже не містить фокус.
  const focusInside = useRef(false);
  const readerFocus = useRef<Event | null>(null);
  useLayoutEffect(() => {
    setArticle((current) => (current && current.scope !== scope ? null : current));
  }, [scope]);
  useLayoutEffect(() => {
    if (!popup) return;
    const track = (event: FocusEvent) => {
      focusInside.current = event === readerFocus.current;
    };
    focusInside.current = popup.contains(document.activeElement);
    document.addEventListener("focusin", track);
    return () => document.removeEventListener("focusin", track);
  }, [popup]);
  // The reader ends above the chat composer, so the person can answer without closing it.
  // Вікно закінчується над полем вводу, щоб відповідати, не закриваючи його.
  useLayoutEffect(() => {
    const conversation = document.getElementById("conversation");
    const composer = conversation?.querySelector<HTMLElement>(".consumer-composer");
    if (!popup || !conversation || !composer) return;
    const measure = () => {
      const composerTop = composer.getBoundingClientRect().top;
      popup.style.setProperty("--composer-top", `${composerTop}px`);
      // On a phone the keyboard pans the visible area down; a reader pinned to the page top
      // would sit above the screen. These two follow what is visible, whatever the browser's
      // coordinate space: the visible top, and the composer's height above the page bottom.
      // На телефоні клавіатура зсуває видиму область; вікно йде за нею й стоїть над полем вводу.
      popup.style.setProperty("--viewport-top", `${window.visualViewport?.offsetTop ?? 0}px`);
      popup.style.setProperty(
        "--composer-gap",
        `${conversation.getBoundingClientRect().bottom - composerTop}px`,
      );
    };
    const stopComposer = observeResize(composer, measure);
    const stopConversation = observeResize(conversation, measure);
    const stopViewport = onViewportResize(measure);
    // iOS scrolls the page after its keyboard opens, which moves the composer but not the reader.
    // iOS прокручує сторінку після появи клавіатури: поле вводу зсувається, а вікно — ні.
    let frame = 0;
    const scrolled = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", scrolled, { passive: true });
    window.visualViewport?.addEventListener("scroll", scrolled, { passive: true });
    measure();
    return () => {
      stopComposer();
      stopConversation();
      stopViewport();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scrolled);
      window.visualViewport?.removeEventListener("scroll", scrolled);
      popup.style.removeProperty("--composer-top");
      popup.style.removeProperty("--viewport-top");
      popup.style.removeProperty("--composer-gap");
    };
  }, [popup]);
  const open = useCallback<OpenArticle>(
    (text, trigger) => {
      setArticle({ scope, text, trigger, open: true });
    },
    [scope],
  );
  const close = useCallback(
    () => setArticle((current) => (current ? { ...current, open: false } : null)),
    [],
  );
  useEffect(() => {
    if (!control) return;
    control.current = { close };
    return () => {
      control.current = null;
    };
  }, [control, close]);
  // Escape in the chat's own composer ends the reading too. The dialog layer takes every Escape
  // for itself, so the key is watched first; a mention list the composer has open closes first.
  // Escape у полі вводу чату теж закриває вікно. Відкритий список згадок закривається першим.
  const readerOpen = article?.scope === scope && article.open;
  useEffect(() => {
    if (!readerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.keyCode === 229) return;
      const target = event.target;
      if (!(target instanceof Element) || !target.closest(".consumer-composer")) return;
      if (target.closest('[aria-expanded="true"]')) return;
      close();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [readerOpen, close]);
  // Focus returns only from the reader itself; a close while typing leaves the composer alone.
  // Фокус повертається лише з вікна; закриття під час набору не забирає його з поля вводу.
  const returnFocus = useCallback(() => {
    if (!focusInside.current) return false;
    return article?.trigger?.isConnected
      ? article.trigger
      : document.getElementById("conversation");
  }, [article?.trigger]);

  return (
    <MessageArticleContext.Provider value={open}>
      {children}
      {/* Non-modal and never dismissed by a press outside: pressing, focusing and typing in the
          composer keep the reader open. It closes with its own button, with Escape, in the
          reader or the composer, and when a message is sent from the composer.
          / Немодальне: натискання й набір у полі вводу не закривають вікно; воно закривається
          кнопкою, Escape та надсиланням повідомлення. */}
      <Dialog modal={false} disablePointerDismissal open={readerOpen}>
        <DialogContent
          ref={setPopup}
          data-testid="message-article-dialog"
          aria-modal={false}
          aria-describedby={undefined}
          showCloseButton={false}
          showOverlay={false}
          initialFocus={closeRef}
          finalFocus={returnFocus}
          onFocus={(event) => {
            readerFocus.current = event.nativeEvent;
          }}
          onKeyDown={(event) => {
            // React bubbles keys from the reader's own menus and table views here, never from
            // the composer; an open menu stops its Escape before it arrives.
            // Сюди доходять клавіші з меню й таблиць вікна, але не з поля вводу.
            if (event.key === "Escape" && !event.nativeEvent.isComposing) close();
          }}
          className="message-reader chat-typography flex h-[100dvh] w-full max-w-none flex-col gap-0 overflow-hidden rounded-none bg-background p-0 text-foreground sm:h-[min(86vh,860px)] sm:w-[min(1080px,calc(100%-3rem))] sm:max-w-[1080px] sm:rounded-2xl sm:bg-card sm:shadow-xl"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5 sm:px-4">
            <button
              ref={closeRef}
              type="button"
              aria-label={t`Close`}
              onClick={close}
              className="flex h-10 min-w-10 shrink-0 items-center gap-1.5 rounded-full px-2 text-foreground/75 outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X size={20} strokeWidth={1.8} />
              <span className="hidden text-[13px] font-medium sm:inline">
                <Trans>Back to chat</Trans>
              </span>
            </button>
            <DialogTitle className="min-w-0 flex-1 truncate text-center text-[14px] font-medium text-foreground">
              <Trans>Full message</Trans>
            </DialogTitle>
            <MessageShareMenu text={article?.text ?? ""} />
          </div>
          <div
            data-testid="message-article-scroll"
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <article
              data-chat-text="body"
              className="mx-auto w-full max-w-[900px] px-5 py-6 pb-[max(env(safe-area-inset-bottom),2rem)] leading-relaxed sm:px-8"
            >
              <ChatMarkdown>{article?.text ?? ""}</ChatMarkdown>
            </article>
          </div>
        </DialogContent>
      </Dialog>
    </MessageArticleContext.Provider>
  );
}
